import firebase from "firebase/app";
import "firebase/storage";
import "firebase/firestore";
import "firebase/functions";

import { firestore, storage } from "../database/firebase";
import type { Author, KnowledgeSource } from "../types/Author";
import {
  attachKnowledgeToAgent,
  computeElevenLabsRagIndex,
  createElevenLabsKbFromFile,
  deleteElevenLabsKbDoc,
  detachKnowledgeFromAgent,
} from "../components/screens/onboarding/OnboardingAPI";

// ThePath cloud functions (vector-store ingestion) deploy to europe-west1, while
// the admin's default functions instance is us-central1. Use a region-scoped
// instance for these calls.
const euFunctions = firebase.app().functions("europe-west1");

type IngestResult = {
  sourceId: string;
  vectorStoreId: string;
  openaiFileId: string;
};

const KNOWLEDGE_STORAGE_PREFIX = "authorKnowledge";

const inferSourceType = (file: File): KnowledgeSource["type"] => {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf") || name.endsWith(".epub")) return "book";
  if (name.endsWith(".txt") || name.endsWith(".md")) return "text";
  if (name.endsWith(".doc") || name.endsWith(".docx")) return "article";
  return "pdf";
};

const sourcesRef = (authorId: string) =>
  firestore.collection("Author").doc(authorId).collection("KnowledgeSource");

/** List an author's knowledge sources, newest first. */
export async function listKnowledgeSources(
  authorId: string
): Promise<KnowledgeSource[]> {
  if (!authorId) return [];
  const snap = await sourcesRef(authorId).orderBy("createdAt", "desc").get();
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as KnowledgeSource));
}

/**
 * Ingest one knowledge source for an author:
 * 1. Upload the raw file to Storage.
 * 2. Index it into the author's OpenAI vector store (text chat retrieval).
 * 3. If the author has a voice agent, attach it to the ElevenLabs KB with RAG.
 *
 * When a voice agent exists, the operation is all-or-nothing from the UI's
 * perspective: a voice failure compensates by deleting the new text source.
 */
export async function ingestKnowledgeSource({
  author,
  file,
}: {
  author: Author;
  file: File;
}): Promise<{ sourceId: string; voiceAttached: boolean; voiceError?: string }> {
  if (!author?.id) throw new Error("Save the author before adding knowledge.");
  if (!file || file.size <= 0) throw new Error("Pick a non-empty file to upload.");

  const type = inferSourceType(file);
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const storagePath = `${KNOWLEDGE_STORAGE_PREFIX}/${author.id}/${Date.now()}_${safeName}`;

  // 1) Upload to Storage so the cloud function can read the bytes.
  await storage.ref().child(storagePath).put(file);

  // 2) Index into the OpenAI vector store (writes the KnowledgeSource doc + sets
  //    Author.knowledgeVectorStoreId server-side).
  const ingest = euFunctions.httpsCallable("ingestAuthorKnowledge", {
    timeout: 540000,
  });
  const { data } = await ingest({
    authorId: author.id,
    storagePath,
    title: file.name,
    type,
    mimeType: file.type || "application/octet-stream",
  });
  const { sourceId } = data as IngestResult;

  // 3) Voice agent knowledge base.
  if (!author.agentId) {
    return { sourceId, voiceAttached: false };
  }
  let voiceDoc: { id: string; name: string } | undefined;
  try {
    voiceDoc = await createElevenLabsKbFromFile({ file, name: file.name });
    await computeElevenLabsRagIndex(voiceDoc.id);
    await attachKnowledgeToAgent({ agentId: author.agentId, doc: voiceDoc });
    await sourcesRef(author.id)
      .doc(sourceId)
      .set(
        {
          elevenLabsDocId: voiceDoc.id,
          elevenLabsDocName: voiceDoc.name,
          elevenLabsDocType: "file",
        },
        { merge: true }
      );
    return { sourceId, voiceAttached: true };
  } catch (error: any) {
    const cleanupErrors: string[] = [];
    if (voiceDoc) {
      await detachKnowledgeFromAgent({
        agentId: author.agentId,
        docId: voiceDoc.id,
      }).catch((cleanupError: any) => {
        cleanupErrors.push(
          cleanupError?.message || "ElevenLabs detach cleanup failed."
        );
      });
      await deleteElevenLabsKbDoc(voiceDoc.id).catch((cleanupError: any) => {
        cleanupErrors.push(
          cleanupError?.message || "ElevenLabs document cleanup failed."
        );
      });
    }
    const remove = euFunctions.httpsCallable("deleteAuthorKnowledge", {
      timeout: 120000,
    });
    await remove({ authorId: author.id, sourceId }).catch(
      (cleanupError: any) => {
        cleanupErrors.push(
          cleanupError?.message || "Text knowledge rollback failed."
        );
      }
    );
    const reason =
      error?.message || "The ElevenLabs voice knowledge sync failed.";
    const cleanupNote = cleanupErrors.length
      ? ` Cleanup also reported: ${cleanupErrors.join(" ")}`
      : "";
    throw new Error(
      `Knowledge was not synchronized, so the ingest was rolled back: ${reason}.${cleanupNote}`
    );
  }
}

/**
 * Delete one knowledge source: detach + delete the ElevenLabs document (if any),
 * then remove the OpenAI file, the stored upload, and the Firestore record.
 */
export async function deleteKnowledgeSource({
  author,
  source,
}: {
  author: Author;
  source: KnowledgeSource;
}): Promise<void> {
  if (!author?.id || !source?.id) return;

  if (source.elevenLabsDocId) {
    if (author.agentId) {
      await detachKnowledgeFromAgent({
        agentId: author.agentId,
        docId: source.elevenLabsDocId,
      });
    }
    await deleteElevenLabsKbDoc(source.elevenLabsDocId);
  } else if (author.agentId) {
    throw new Error(
      "This source is missing its ElevenLabs document id. Refusing a one-sided delete."
    );
  }

  const remove = euFunctions.httpsCallable("deleteAuthorKnowledge", {
    timeout: 120000,
  });
  await remove({ authorId: author.id, sourceId: source.id });
}
